import React from 'react';
import { Link } from 'react-router-dom';
import ContentPageLayout from './components/ContentPageLayout';

const TermsPage = () => {
  return (
    <ContentPageLayout
      eyebrow="TECNIQ policies"
      title="Terms of Use"
      intro="The rules that govern access to TECNIQ across the website and mobile experience."
      lastUpdated="February 4, 2026"
      companionLink={{ to: '/privacy', label: 'View Privacy Policy' }}
    >
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Acceptance of These Terms</h2>
                    <p>
                        These Terms of Use ("Terms") govern your access to and use of Tecniq, including our mobile application
                        and our website (together, the "Service"). By accessing or using the Service, you agree to be bound by
                        these Terms. If you do not agree, do not use the Service.
                    </p>
      </section>

      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Who We Are</h2>
                    <p>
                        "Tecniq", "we", "us", and "our" refer to the Tecniq team. You can contact us at{' '}
                        <a href="mailto:main@tecniq.com.au">main@tecniq.com.au</a>.
                    </p>
      </section>

      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Eligibility</h2>
                    <p>
                        You must be able to form a legally binding contract in your jurisdiction to use the Service. If you are
                        under the age of majority where you live, you may use the Service only with permission of a parent or legal
                        guardian.
                    </p>
      </section>

      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Your Account</h2>
                    <p>
                        Some features may require you to create an account. You are responsible for maintaining the confidentiality
                        of your login credentials and for all activity that occurs under your account. You agree to provide accurate
                        information and to keep it up to date.
                    </p>
      </section>

      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Subscriptions, Trials, Billing, and Cancellation</h2>
                    <p>
                        Tecniq may offer paid subscriptions (for example, "Tecniq Pro") that unlock additional features. If you
                        purchase a subscription through the Apple App Store or Google Play Store, billing and subscription management
                        are handled by the applicable store and are subject to that store’s terms.
                    </p>
                    <ul style={{ marginLeft: 24 }}>
                        <li><strong>Auto-renewal:</strong> Subscriptions may automatically renew unless cancelled before the renewal date.</li>
                        <li><strong>Managing your subscription:</strong> You can cancel or manage your subscription in your device’s subscription settings.</li>
                        <li><strong>Free trials:</strong> If a free trial is offered, it may convert to a paid subscription at the end of the trial unless cancelled beforehand.</li>
                        <li><strong>Refunds:</strong> Refund requests are handled by the store where you made your purchase and are subject to that store’s policies.</li>
                    </ul>
                    <p>
                        We may change subscription offerings and pricing over time, as permitted by applicable law and store policies.
                    </p>
      </section>

      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Health and Fitness Disclaimer</h2>
                    <p>
                        The Service provides tools for workout tracking and general fitness information. It is not medical advice.
                        Always consult a qualified healthcare professional before starting a new exercise program, especially if you
                        have any medical conditions, injuries, or concerns.
                    </p>
                    <p>
                        You use the Service at your own risk. You are responsible for your own health and safety while exercising.
                    </p>
      </section>

      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Your Data</h2>
                    <p>
                        Tecniq is designed to be local-first. Many types of workout data are stored on your device, and
                        some supported features may also allow optional cloud backup, sync, or media storage when enabled.
                        For details about how we handle data, please review our <Link to="/privacy">Privacy Policy</Link>.
                    </p>
      </section>

      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>License and Intellectual Property</h2>
                    <p>
                        The Service, including its software, design, text, graphics, logos, and other content (excluding content you
                        provide), is owned by Tecniq or its licensors and is protected by intellectual property laws.
                    </p>
                    <p>
                        We grant you a limited, non-exclusive, non-transferable, revocable license to use the Service for your
                        personal, non-commercial use in accordance with these Terms.
                    </p>
      </section>

      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Prohibited Conduct</h2>
                    <p>You agree not to:</p>
                    <ul style={{ marginLeft: 24 }}>
                        <li>Use the Service in any way that violates applicable laws or regulations</li>
                        <li>Reverse engineer, decompile, or attempt to extract source code except where permitted by law</li>
                        <li>Interfere with or disrupt the Service, servers, or networks</li>
                        <li>Attempt to gain unauthorized access to accounts or systems</li>
                        <li>Use the Service to transmit malware or harmful code</li>
                    </ul>
      </section>

      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Third-Party Services</h2>
                    <p>
                        The Service may interact with third-party services (for example, Apple App Store, Google Play, payment and
                        subscription infrastructure providers). Your use of those third-party services may be governed by their own
                        terms and policies. We are not responsible for third-party services.
                    </p>
      </section>

      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Disclaimers</h2>
                    <p>
                        The Service is provided on an “as is” and “as available” basis. To the maximum extent permitted by law, we
                        disclaim all warranties of any kind, whether express, implied, or statutory, including implied warranties of
                        merchantability, fitness for a particular purpose, and non-infringement.
                    </p>
                    <p>
                        We do not guarantee that the Service will be uninterrupted, secure, or error-free, or that any particular
                        results will be achieved through use of the Service.
                    </p>
      </section>

      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Limitation of Liability</h2>
                    <p>
                        To the maximum extent permitted by law, Tecniq and its affiliates, officers, employees, agents, and licensors
                        will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of
                        profits, revenues, data, or goodwill arising out of or related to your use of the Service.
                    </p>
      </section>

      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Indemnity</h2>
                    <p>
                        You agree to indemnify and hold harmless Tecniq and its affiliates, officers, employees, and agents from and
                        against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising from
                        or related to your use of the Service or your violation of these Terms.
                    </p>
      </section>

      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Termination</h2>
                    <p>
                        We may suspend or terminate your access to the Service at any time if we reasonably believe you have violated
                        these Terms or if necessary to protect the Service, other users, or Tecniq. You may stop using the Service at
                        any time.
                    </p>
      </section>

      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Changes to These Terms</h2>
                    <p>
                        We may update these Terms from time to time. If we make material changes, we will take reasonable steps to
                        provide notice (for example, by posting an updated version on our website). Your continued use of the Service
                        after the effective date of the updated Terms constitutes acceptance.
                    </p>
      </section>

      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Governing Law</h2>
                    <p>
                        These Terms are governed by the laws of Australia, without regard to conflict of laws principles.
                    </p>
      </section>

      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Contact Us</h2>
                    <p>
                        Questions about these Terms? Contact us at{' '}
                        <a href="mailto:main@tecniq.com.au">main@tecniq.com.au</a>{' '}
                        or via our <Link to="/#contact">contact page</Link>.
                    </p>
      </section>
    </ContentPageLayout>
  );
};

export default TermsPage;
